import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { usePlatform } from '~/services/platform';
import { kioskStore } from '~/stores/kiosk.store';
import { streamStore } from '~/stores';
import { openAndroidSettings } from '~/services/platform/kioskMode';

const Button = styled.button`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 10000;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: none;
  background: rgba(33, 33, 33, 0.92);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:active {
    transform: scale(0.94);
  }

  svg {
    width: 34px;
    height: 34px;
    fill: #ffffff;
  }
`;

/**
 * KioskSettingsButton (v1.0.142)
 *
 * Botão "Abrir Configurações do Android" — saída determinística pro operador
 * após desativar o kiosk (7-click + PIN no player OU comando do admin). Dispara
 * o Intent {@code Settings.ACTION_SETTINGS} via plugin nativo, SEM depender de
 * nav bar / Home / launcher (que num device Device Owner ficam frágeis — caso
 * "Para sua análise" em loop relatado pelo Renato).
 *
 * Regras (Renato 2026-06-05):
 * - SÓ em Android (TV Tizen/webOS e Browser não mostram).
 * - SÓ aparece com kiosk DESATIVADO ({@code isLocked === false}).
 * - Canto inferior direito. Vale em TODAS as telas (iniciais "aguardando
 *   conexão/conteúdo" e durante mídia). Com kiosk OFF a nav bar fica persistente
 *   (v1.0.140), então o critério "kiosk off" já cobre o caso de mídia com o
 *   painel do Android visível.
 */
const KioskSettingsButton: React.FC = observer(() => {
  const platform = usePlatform();

  // v1.0.144 — critério correto de "está tocando mídia agora": presença de um
  // item ativo (currentMedia). NÃO usar syncStore.hasContent: ele fica TRUE só
  // por haver arquivos em CACHE, então na tela "aguardando conteúdo" (sem item
  // ativo, mas com cache) ele escondia o botão indevidamente.
  //
  // Regra (Renato): telas iniciais / aguardando conexão / aguardando conteúdo
  // (sem mídia tocando) → mostra sempre que kiosk OFF. Durante a MÍDIA → só com
  // o painelzinho de controle do player aberto (controlsVisible).
  const isPlayingMedia = streamStore.currentMedia !== null;
  const visible =
    platform.platform === 'android' &&
    !kioskStore.isLocked &&
    (!isPlayingMedia || streamStore.controlsVisible);

  const handleOpen = useCallback(() => {
    void openAndroidSettings();
  }, []);

  if (!visible) return null;

  return (
    <Button
      type="button"
      aria-label="Abrir Configurações do Android"
      data-testid="kiosk-open-settings"
      onClick={handleOpen}
      onTouchEnd={(e) => {
        e.preventDefault();
        handleOpen();
      }}
    >
      {/* Ícone de engrenagem (estilo Configurações do Android) */}
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.49.49 0 0 0-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.49.49 0 0 0-.48-.41h-3.84a.49.49 0 0 0-.48.41l-.36 2.54c-.59.24-1.12.56-1.62.94l-2.39-.96a.49.49 0 0 0-.59.22L2.74 8.87a.49.49 0 0 0 .12.61l2.03 1.58c-.04.31-.06.63-.06.94 0 .31.02.63.06.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.13.22.38.3.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.04.24.24.41.48.41h3.84c.24 0 .44-.17.48-.41l.36-2.54c.59-.24 1.12-.56 1.62-.94l2.39.96c.21.08.46 0 .59-.22l1.92-3.32a.49.49 0 0 0-.12-.61l-2.03-1.58ZM12 15.6a3.6 3.6 0 1 1 0-7.2 3.6 3.6 0 0 1 0 7.2Z" />
      </svg>
    </Button>
  );
});

export default KioskSettingsButton;
